import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Button,
    Backdrop,
    CircularProgress,
    Theme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import RiderServiceInstance from '../../services/RiderService';
import EventServiceInstance from '../../services/EventService';
import CircuitServiceInstance from '../../services/CircuitService';
import { IRiderRead } from '../../models/IRider';
import ConfirmDialog from '../common/ConfirmDialog';
import AuthContext from '../../services/Auth/AuthContext';
import Unauthorized from '../common/Unauthorized';
import config from '../../config';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        appBarSpacer: theme.mixins.toolbar,
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        saveButton: {
            marginTop: theme.spacing(2),
        },
    })
);

interface IReadWithElapsed {
    read: IRiderRead;
    elapsedMs: number | null;
}

// C# ticks are 100-nanosecond intervals since Jan 1, 0001 UTC.
// Unix epoch (Jan 1, 1970) expressed as C# ticks is 621,355,968,000,000,000.
// Offset in ms = 621355968000000000 / 10000 = 62135596800000
const DOTNET_EPOCH_OFFSET_MS = 62135596800000;
const TICKS_PER_MS = 10000;

function ticksToUnixMs(ticks: number): number {
    return ticks / TICKS_PER_MS - DOTNET_EPOCH_OFFSET_MS;
}

function formatReadTime(ticks: number, timeZone?: string): string {
    const unixMs = ticksToUnixMs(ticks);
    const date = new Date(unixMs);
    if (isNaN(date.getTime())) {
        return String(ticks);
    }
    // Milliseconds are timezone-independent
    const ms = String(date.getUTCMilliseconds()).padStart(3, '0');
    const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    };
    if (timeZone) {
        options.timeZone = timeZone;
    }
    try {
        return `${new Intl.DateTimeFormat('en-GB', options).format(date)}.${ms}`;
    } catch {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}.${ms}`;
    }
}

function formatElapsed(ms: number | null): string {
    if (ms === null) {
        return '-';
    }
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = ms % 1000;
    return `${minutes}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
}

function computeElapsed(reads: IRiderRead[]): IReadWithElapsed[] {
    return reads.map((read, index) => {
        if (index === 0) {
            return { read, elapsedMs: null };
        }
        const prevTicks = reads[index - 1].tagSeenTime;
        const currTicks = read.tagSeenTime;
        return { read, elapsedMs: (currTicks - prevTicks) / TICKS_PER_MS };
    });
}

function RiderReadsPage() {
    const { raceId, tagId } = useParams<{ raceId: string; tagId: string }>();
    const { t } = useTranslation('common');
    const classes = useStyles();
    const authService = useContext(AuthContext);

    const [reads, setReads] = useState<IRiderRead[]>([]);
    const [eventName, setEventName] = useState<string>('');
    const [circuitTimeZone, setCircuitTimeZone] = useState<string | undefined>(undefined);
    const [pendingDeletions, setPendingDeletions] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const isAdmin = authService?.isInRole(config.managementGroups.administrator) ?? false;

    useEffect(() => {
        if (!raceId || !tagId) {
            return;
        }
        const source = axios.CancelToken.source();
        const fetchData = async () => {
            const [fetchedReads, event] = await Promise.all([
                RiderServiceInstance.getRiderReads(raceId, tagId, source.token),
                EventServiceInstance.getEvent(raceId, source.token),
            ]);
            setReads(fetchedReads);
            setEventName(event?.name ?? '');
            if (event?.circuitId) {
                const circuit = await CircuitServiceInstance.getCircuit(event.circuitId, source.token);
                if (circuit?.timeZone) {
                    setCircuitTimeZone(circuit.timeZone);
                }
            }
            setLoading(false);
        };
        fetchData().catch((error) => {
            if (!axios.isCancel(error)) {
                console.error(error);
                setLoading(false);
            }
        });
        return () => {
            source.cancel();
        };
    }, [raceId, tagId]);

    if (!isAdmin) {
        return <Unauthorized />;
    }

    const visibleReads = reads.filter((r) => !pendingDeletions.has(r.tagSeenTime));
    const readsWithElapsed = computeElapsed(visibleReads);

    const handleDelete = (tagSeenTime: number) => {
        setPendingDeletions((prev) => {
            const next = new Set(prev);
            next.add(tagSeenTime);
            return next;
        });
    };

    const handleSave = () => {
        if (pendingDeletions.size > 0) {
            setConfirmOpen(true);
        }
    };

    const handleConfirm = async () => {
        setConfirmOpen(false);
        setSaving(true);
        try {
            for (const tagSeenTime of Array.from(pendingDeletions)) {
                const read = reads.find((r) => r.tagSeenTime === tagSeenTime);
                if (read) {
                    await RiderServiceInstance.deleteRiderRead(raceId!, read.epc, tagSeenTime);
                } else {
                    console.error(`Read with tagSeenTime ${tagSeenTime} not found; skipping deletion.`);
                }
            }
            setReads((prev) => prev.filter((r) => !pendingDeletions.has(r.tagSeenTime)));
            setPendingDeletions(new Set());
        } catch (error) {
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelConfirm = () => {
        setConfirmOpen(false);
    };

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Box>
                    <OntimeBreadcrumb nameMap={{ [t('events.organizerEvents')]: '/events/organizer', [eventName]: `/event/setup/${raceId}` }} current={t('riderReads.title')} />
                <Typography variant="h5" gutterBottom>
                    {t('riderReads.title')}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                    {tagId}
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small" aria-label="rider reads table">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('riderReads.sourceTag')}</TableCell>
                                <TableCell>{t('riderReads.timeRead')}</TableCell>
                                <TableCell>{t('riderReads.elapsed')}</TableCell>
                                <TableCell>{t('riderReads.readerId')}</TableCell>
                                <TableCell>{t('riderReads.antennaPort')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {readsWithElapsed.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={6}>{t('riderReads.noReads')}</TableCell>
                                </TableRow>
                            )}
                            {readsWithElapsed.map(({ read, elapsedMs }) => (
                                <TableRow key={read.tagSeenTime}>
                                    <TableCell>{read.epc}</TableCell>
                                    <TableCell>{formatReadTime(read.tagSeenTime, circuitTimeZone)}</TableCell>
                                    <TableCell>{formatElapsed(elapsedMs)}</TableCell>
                                    <TableCell>{read.readerId}</TableCell>
                                    <TableCell>{read.antennaPortNumber}</TableCell>
                                    <TableCell align="right" padding="none">
                                        <Tooltip title={t('commonLabels.delete') as string}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(read.tagSeenTime)}
                                                aria-label={t('commonLabels.delete') as string}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pendingDeletions.size > 0 && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        className={classes.saveButton}
                    >
                        {t('riderReads.saveChanges')}
                    </Button>
                )}
                <ConfirmDialog
                    open={confirmOpen}
                    title={t('riderReads.confirmDeleteTitle')}
                    message={t('riderReads.confirmDeleteMessage')}
                    onAccept={handleConfirm}
                    onCancel={handleCancelConfirm}
                />
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={saving || loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
                </Box>
            </Container>
        </main>
    );
}

export default RiderReadsPage;
